/**
 * Shared per-branch bill analysis used by both Bill Reports and Sales Reports'
 * "Branch Analysis" sections: given a flat array of bill rows (from filtter_billslist —
 * branch_id/branch, invoice_no, status, final_mop, discount, total_paid_amount), groups
 * them by branch and computes invoice-sequence gaps, Cancelled/NC/Credit/Discount splits,
 * total sales, and average bill value.
 */
import moment from 'moment';

export const normalizeMop = (m) =>
    String(m ?? '')
        .trim()
        .toUpperCase();

export const isCancelledStatus = (row) => (row.status || '').toLowerCase() === 'cancelled';

export const isCreditMop = (row) => {
    const m = normalizeMop(row.final_mop);
    return m === 'CREDIT' || m.includes('CREDIT');
};

/** Splits an invoice_no like "SB12627056470" into its letter prefix and numeric tail, preserving the tail's zero-padding width. */
export const parseInvoiceNo = (invoiceNo) => {
    const match = String(invoiceNo || '').match(/^(\D*)(\d+)$/);
    if (!match) return null;
    return { prefix: match[1], width: match[2].length, value: parseInt(match[2], 10) };
};

/** Cap on how many missing invoice numbers get listed in a hover popup — a branch's own numbering could run very long. */
export const MAX_MISSING_INVOICES_LISTED = 200;

/**
 * Safety cap on the gap-detection scan below (max - min invoice number values). That loop is
 * O(range), not O(bill count) — a branch whose invoice numbers aren't small tight sequences
 * (e.g. they embed a date/timestamp, or two branches' ranges get grouped together) can put
 * min/max millions apart even with only a handful of real bills, which would otherwise freeze
 * the browser tab scanning a range that large. Past this cap, gaps just aren't reported rather
 * than computed at any cost — Branch Analysis still shows every other stat normally.
 */
export const MAX_INVOICE_GAP_SCAN_RANGE = 200000;

/**
 * One branch's worth of bill rows -> its Branch Analysis stats. Rows are expected
 * pre-sorted `invoice_date DESC, bill_id DESC` (most recent first, the backend's own
 * ordering) so the earliest/latest invoice_no can be read as the last/first row.
 */
export const computeBranchBillStats = (rows) => {
    const cancelledRows = rows.filter(isCancelledStatus);
    const ncRows = rows.filter((r) => normalizeMop(r.final_mop) === 'NC');
    const creditRows = rows.filter(isCreditMop);
    const discountRows = rows.filter((r) => parseFloat(r.discount || 0) > 0);

    const totalSales = rows.reduce((sum, r) => sum + parseFloat(r.total_paid_amount || 0), 0);

    // Invoice numbers are expected to run consecutively within a branch — any number
    // between the lowest and highest seen here that isn't present is a gap (a bill
    // that was fully deleted rather than just cancelled).
    const parsedInvoices = rows.map((r) => parseInvoiceNo(r.invoice_no)).filter(Boolean);
    let invoiceHasGap = false;
    let invoiceMissingCount = 0;
    let invoiceMissingList = [];
    if (parsedInvoices.length > 1) {
        const values = parsedInvoices.map((p) => p.value);
        const seen = new Set(values);
        const min = values.reduce((a, b) => Math.min(a, b), values[0]);
        const max = values.reduce((a, b) => Math.max(a, b), values[0]);
        const { prefix, width } = parsedInvoices[0];
        if (max - min <= MAX_INVOICE_GAP_SCAN_RANGE) {
            for (let v = min; v <= max; v++) {
                if (!seen.has(v)) {
                    invoiceMissingCount += 1;
                    if (invoiceMissingList.length < MAX_MISSING_INVOICES_LISTED) {
                        invoiceMissingList.push(`${prefix}${String(v).padStart(width, '0')}`);
                    }
                }
            }
            invoiceHasGap = invoiceMissingCount > 0;
        }
    }

    return {
        invoiceStart: rows[rows.length - 1]?.invoice_no || '',
        invoiceEnd: rows[0]?.invoice_no || '',
        invoiceHasGap,
        invoiceMissingCount,
        invoiceMissingList,
        totalBills: rows.length,
        cancelledCount: cancelledRows.length,
        ncCount: ncRows.length,
        creditCount: creditRows.length,
        discountCount: discountRows.length,
        totalSales,
        cancelledAmount: cancelledRows.reduce((sum, r) => sum + parseFloat(r.total_paid_amount || 0), 0),
        ncAmount: ncRows.reduce((sum, r) => sum + parseFloat(r.total_paid_amount || 0), 0),
        creditAmount: creditRows.reduce((sum, r) => sum + parseFloat(r.total_paid_amount || 0), 0),
        discountAmount: discountRows.reduce((sum, r) => sum + parseFloat(r.discount || 0), 0),
        avgBillValue: rows.length ? totalSales / rows.length : 0
    };
};

/** Groups bill rows by branch_id/branch, sorted alphabetically by branch name. */
export const buildBranchAnalysisRows = (rows) => {
    if (!rows || !rows.length) return [];

    const groups = new Map();
    rows.forEach((row) => {
        const key = String(row.branch_id ?? row.branch ?? '').trim() || 'UNKNOWN';
        if (!groups.has(key)) {
            groups.set(key, { branchId: key, branchName: row.branch || row.branch_title || 'Unknown', rows: [] });
        }
        groups.get(key).rows.push(row);
    });

    return Array.from(groups.values())
        .map(({ branchId, branchName, rows: branchRows }) => ({ branchId, branchName, ...computeBranchBillStats(branchRows) }))
        .sort((a, b) => a.branchName.localeCompare(b.branchName, undefined, { sensitivity: 'base' }));
};

/**
 * Generic version of buildBranchAnalysisRows — groups bill rows by any key/label (cashier,
 * waiter, ...) instead of branch. Each group keeps its own raw `rows` alongside the computed
 * stats, so a caller can group again one level deeper (e.g. a cashier's rows split by waiter).
 * `statsFn` defaults to the full computeBranchBillStats (Cancelled/NC/Credit/Discount split +
 * invoice-gap detection) but callers that only need totals — Cashier Sales' tree below, which
 * re-groups the same rows at every level from Day down to Waiter — should pass the cheaper
 * computeBillTotals instead, since re-running gap detection at every node is pure waste when
 * nothing ever reads it there.
 */
export const groupBillRows = (rows, getKey, getLabel, statsFn = computeBranchBillStats) => {
    if (!rows || !rows.length) return [];

    const groups = new Map();
    rows.forEach((row) => {
        const key = getKey(row);
        if (!groups.has(key)) {
            groups.set(key, { key, label: getLabel(row), rows: [] });
        }
        groups.get(key).rows.push(row);
    });

    return Array.from(groups.values())
        .map(({ key, label, rows: groupRows }) => ({ key, label, rows: groupRows, ...statsFn(groupRows) }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
};

/**
 * Cheaper alternative to computeBranchBillStats for callers that only need the bare totals
 * (invoice range, bill count, sales) — skips the Cancelled/NC/Credit/Discount classification
 * and, critically, the invoice-gap scan, which is O(max_invoice_number - min_invoice_number)
 * and can silently explode into millions of iterations (freezing the tab) once rows from more
 * than one branch/numbering sequence get grouped together, even with a small bill count.
 */
export const computeBillTotals = (rows) => ({
    invoiceStart: rows[rows.length - 1]?.invoice_no || '',
    invoiceEnd: rows[0]?.invoice_no || '',
    totalBills: rows.length,
    totalSales: rows.reduce((sum, r) => sum + parseFloat(r.total_paid_amount || 0), 0)
});

/**
 * Cashier Sales page: Day -> [Branch ->] Cashier -> Waiter tree, built from the same bill-level
 * rows Branch Analysis uses — grouped by real calendar date, since invoice numbers only exist
 * at the bill level (unlike the half-day session API Session Sales reads). Each node's stats
 * (Bill Starting/Ending, Total Bills, Sales Amount) come from computeBillTotals — this tree
 * never shows gap info, and a node as high as "Day" can span every branch's bills at once, so
 * running the full gap scan here is both wasted and the thing that can freeze the page (see
 * computeBillTotals above).
 */
const cashierNodeData = (label, level, statsRows) => {
    const stats = computeBillTotals(statsRows);
    return { label, level, invoiceStart: stats.invoiceStart, invoiceEnd: stats.invoiceEnd, totalBills: stats.totalBills, totalSales: stats.totalSales };
};

const buildCashierNodes = (dataRows, keyPrefix) =>
    groupBillRows(
        dataRows,
        (r) => String(r.userId ?? r.waiter_no ?? '').trim() || 'UNKNOWN',
        (r) => r.waiter_name || (r.userId ? `ID: ${r.userId}` : 'Unassigned'),
        computeBillTotals
    ).map((cashier) => {
        const cashierKeyPrefix = `${keyPrefix}-u-${cashier.key}`;
        const waiterNodes = groupBillRows(
            cashier.rows,
            (r) => String(r.waiter_no ?? '').trim() || 'UNASSIGNED',
            (r) => r.table_waiter_name || 'Unassigned',
            computeBillTotals
        ).map((waiter) => ({
            key: `${cashierKeyPrefix}-w-${waiter.key}`,
            data: cashierNodeData(waiter.label, 'waiter', waiter.rows)
        }));
        return {
            key: cashierKeyPrefix,
            data: cashierNodeData(cashier.label, 'user', cashier.rows),
            children: waiterNodes
        };
    });

export const buildCashierSalesTree = (rows, formatDateFn) => {
    if (!rows.length) return [];

    const showBranchLevel = new Set(rows.map((r) => String(r.branch_id ?? r.branch ?? '').trim() || 'UNKNOWN')).size > 1;

    const rowsByDate = {};
    rows.forEach((r) => {
        (rowsByDate[r.invoice_date || ''] ||= []).push(r);
    });

    const dateKeys = Object.keys(rowsByDate).sort((a, b) => {
        const da = moment(a, ['DD-MM-YYYY', 'YYYY-MM-DD'], true);
        const db = moment(b, ['DD-MM-YYYY', 'YYYY-MM-DD'], true);
        return db.valueOf() - da.valueOf();
    });

    return dateKeys.map((dateKey) => {
        const dayRows = rowsByDate[dateKey];

        let children;
        if (showBranchLevel) {
            children = groupBillRows(
                dayRows,
                (r) => String(r.branch_id ?? r.branch ?? '').trim() || 'UNKNOWN',
                (r) => r.branch || r.branch_title || 'Unknown',
                computeBillTotals
            ).map((branch) => {
                const branchKeyPrefix = `${dateKey}-b-${branch.key}`;
                return {
                    key: branchKeyPrefix,
                    data: cashierNodeData(branch.label, 'branch', branch.rows),
                    children: buildCashierNodes(branch.rows, branchKeyPrefix)
                };
            });
        } else {
            children = buildCashierNodes(dayRows, dateKey);
        }

        return {
            key: dateKey,
            data: cashierNodeData(formatDateFn(dateKey), 'day', dayRows),
            children
        };
    });
};
